const CSS = {
  hidden: 'command-palette--hidden',
  result: 'command-palette__result',
  resultActive: 'command-palette__result--active',
  resultName: 'command-palette__result-name',
  resultSubtitle: 'command-palette__result-subtitle',
  resultShortcut: 'command-palette__result-shortcut',
  error: 'command-palette__error',
  noResults: 'command-palette__no-results',
}

export default class CommandPalette {
  constructor({ dialog, input, results, commands, messages }) {
    this.dialog = dialog
    this.input = input
    this.results = results
    this.commands = commands
    this.messages = messages
    this.mode = 'closed'
    this.searchPath = null
    this.searchAbortController = null
  }

  isOpen() {
    return !this.dialog.classList.contains(CSS.hidden)
  }

  openCommandList() {
    this.mode = 'command-list'
    this.searchPath = null
    this.showDialog(this.messages.placeholder)
    this.renderCommandList(this.commands)
  }

  openSearch(searchPath, placeholder) {
    this.mode = 'search'
    this.searchPath = searchPath
    this.showDialog(placeholder)
    this.clearResults()
  }

  close() {
    this.abortInFlightSearch()
    this.mode = 'closed'
    this.searchPath = null
    this.dialog.classList.add(CSS.hidden)
    this.input.value = ''
    this.clearResults()
  }

  navigateDown() {
    this.moveActive(1)
  }

  navigateUp() {
    this.moveActive(-1)
  }

  activeElement() {
    return this.results.querySelector(`.${CSS.resultActive}`)
  }

  activeCommandKey() {
    if (this.mode !== 'command-list') return null

    return this.activeElement()?.dataset.commandKey ?? null
  }

  async handleInput(query) {
    if (this.mode === 'command-list') {
      this.filterCommandList(query)
    } else if (this.mode === 'search') {
      await this.performSearch(query)
    }
  }

  filterCommandList(query) {
    const lowerQuery = query.trim().toLowerCase()
    if (lowerQuery.length === 0) {
      this.renderCommandList(this.commands)
      return
    }

    const filtered = this.commands.filter(command =>
      command.name.toLowerCase().includes(lowerQuery) || command.keyBinding.toLowerCase().includes(lowerQuery)
    )
    this.renderCommandList(filtered)
  }

  async performSearch(query) {
    this.abortInFlightSearch()

    if (query.length === 0 || !this.searchPath) {
      this.clearResults()
      return
    }

    const controller = new AbortController()
    this.searchAbortController = controller

    try {
      const results = await this.fetchResults(query, controller.signal)
      if (this.searchAbortController === controller) this.renderSearchResults(results)
    } catch (error) {
      if (error.name === 'AbortError') return
      console.error('Command palette search failed', error)
      if (this.searchAbortController === controller) this.renderMessage(this.messages.searchError, CSS.error)
    }
  }

  abortInFlightSearch() {
    if (this.searchAbortController) {
      this.searchAbortController.abort()
      this.searchAbortController = null
    }
  }

  showDialog(placeholder) {
    this.dialog.classList.remove(CSS.hidden)
    this.input.value = ''
    this.input.placeholder = placeholder
    this.input.focus()
  }

  clearResults() {
    this.results.innerHTML = ''
  }

  moveActive(direction) {
    const items = Array.from(this.results.querySelectorAll(`.${CSS.result}`))
    if (items.length === 0) return

    const currentIndex = items.indexOf(this.activeElement())
    const newIndex = Math.min(Math.max(currentIndex + direction, 0), items.length - 1)

    items.forEach((item, index) => item.classList.toggle(CSS.resultActive, index === newIndex))
    items[newIndex].scrollIntoView({ block: 'nearest' })
  }

  renderCommandList(commands) {
    if (commands.length === 0) {
      this.renderMessage(this.messages.noCommands, CSS.noResults)
      return
    }

    this.results.innerHTML = commands.map((command, index) => `
      <button type="button"
              class="${CSS.result} ${index === 0 ? CSS.resultActive : ''}"
              data-command-key="${escapeHtml(command.key)}"
              data-action="click->command-palette#executeActiveCommand">
        <span class="${CSS.resultName}">${escapeHtml(command.name)}</span>
        <span class="${CSS.resultShortcut}">${escapeHtml(command.shortcut)}</span>
      </button>
    `).join('')
  }

  renderSearchResults(results) {
    if (results.length === 0) {
      this.renderMessage(this.messages.noResults, CSS.noResults)
      return
    }

    this.results.innerHTML = results.map((result, index) => `
      <a href="${escapeHtml(result.url)}"
         class="${CSS.result} ${index === 0 ? CSS.resultActive : ''}"
         data-action="click->command-palette#visitResult">
        <span class="${CSS.resultName}">${escapeHtml(result.name)}</span>
        ${result.subtitle ? `<span class="${CSS.resultSubtitle}">${escapeHtml(result.subtitle)}</span>` : ''}
      </a>
    `).join('')
  }

  renderMessage(message, className) {
    this.results.innerHTML = `<div class="${className}">${escapeHtml(message)}</div>`
  }

  async fetchResults(query, signal) {
    const url = new URL(this.searchPath, window.location.origin)
    url.searchParams.set('q', query)

    const response = await fetch(url, {
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      signal
    })

    if (!response.ok) throw new Error(`Search failed with status ${response.status}`)

    return response.json()
  }
}

function escapeHtml(text) {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}
